import {useList, useUpdateEffect} from 'react-use'
import {Dispatch, SetStateAction, useEffect, useMemo, useState} from 'react'
import {Controller, SubmitHandler} from 'react-hook-form'
import Joi from 'joi'
import {createDispatchActions, selectMethod, selectProjectData, selectTempTaskData, selectTheme} from '../../store'
import {
  _,
  collaboratorTypeHasRole,
  createTaskColorTranslator,
  RequestResult,
  RIF,
  t,
  useCollaboratorTypeInProject,
  useCurrentProjectState,
  useCurrentWorkspaceState,
  useForm,
  v,
} from '../../lib'
import {DeviceIcon, SurveyBlackIcon, TimerBlackIcon, TodoBlackIcon} from '../../asset/image'
import MovesenseImg from '../../asset/image/movesense.png'
import {
  EditingTask,
  getTaskContentName,
  TempTask,
  tempStopwatchMovesenseStreamSpec,
  tempStopwatchGarminStreamSpec,
  TempStopwatchMovesenseStream,
  TempStopwatchGarminStream,
  TempQuestionnaire,
  TempQuestionnaireDisplayStrategyUpsertItem,
  sanitizeDuplicatedSection,
  fetchDisplayStrategyListFromQuestionnaire,
  normalizeTaskScheduleReminderList,
  TaskScheduleReminderDTO,
} from '../../model'
import {
  AddTaskAppPreviewSection,
  AddTaskGarminStreamSection,
  AddTaskMovesenseSection,
  AddTaskReminderSection,
  AddTaskScheduleSection,
  Button,
  ButtonCancel,
  ColorSelector,
  Input,
  LabelBox,
  PopupConfirmDeleteTask,
  PopupConfirmDiscardTask,
  PopupConfirmShowTask,
  RequiredTag,
  Textarea,
  TextLink,
  QuestionnairePage,
  ButtonReverse,
  PopupDuplicateExistingQuestionnaire,
} from '..'
import {
  IProjectScheduleConfig,
  TaskType,
  ITaskSchedule,
  ProjectCollaboratorRole,
  WorkspaceCollaboratorRole,
  IStopwatchMovesenseStream,
  ITask,
  IStopwatchGarminStream,
  IQuestionnaire,
  ReminderItem,
} from '../../shared/db'
import {TaskScheduleSection} from './task_schedule_section'
import {
  TempTaskSchedule,
  TaskScheduleSettingType,
  ActionableTempTaskSchedule,
  TempTaskScheduleActionType,
} from '../../model'
import {isNil} from 'lodash'
import {DeepPartial} from 'utility-types'

const taskScheduleSettingTypeToDisplayString = (taskScheduleSettingType: TaskScheduleSettingType): string | null => {
  switch (taskScheduleSettingType) {
    case TaskScheduleSettingType.Legacy:
      return null
    case TaskScheduleSettingType.Schedule:
      return 'Scheduled Task'
    case TaskScheduleSettingType.EventTracker:
      return 'Event Tracking Task'
  }
}

export const taskPlaceholder = (taskType: TaskType) => {
  let taskNamePlaceholder = ''
  let taskDescriptionPlaceholder = ''
  switch (taskType) {
    case TaskType.Todo:
      taskNamePlaceholder = 'Morning Intervention'
      taskDescriptionPlaceholder =
        'After taking morning dose of intervention, mark this as complete to keep an accurate timestamp.'
      break
    case TaskType.Timer:
      taskNamePlaceholder = 'Run 5k'
      taskDescriptionPlaceholder =
        'On a treadmill with no incline, time your 5k run. Hit start at the beginning and end to submit the final time.'
      break
    case TaskType.Questionnaire:
      taskNamePlaceholder = 'Baseline Questionnaire'
      taskDescriptionPlaceholder =
        'Thank you for being a part of this research. This questionnaire will help us understand your waking habits.'
      break
    case TaskType.StopwatchGarminStream:
      taskNamePlaceholder = 'Golf Swing Practice'
      taskDescriptionPlaceholder = 'Acc text'
      break
    case TaskType.StopwatchMovesenseStream:
    case TaskType.StopwatchMovesenseLogData:
      taskNamePlaceholder = 'Tremor Test'
      taskDescriptionPlaceholder =
        'Using the attached wristband accessory, wear the Movesense device on your right wrist. Make sure the fit is snug enough that any movement of the wrist will not shake or move the Movesense device. Press start once the fit is correct, and measure for 30 minutes.'
      break
  }
  return {taskNamePlaceholder, taskDescriptionPlaceholder}
}

interface FormData {
  name?: string
  description?: string
  scheduleRepeat?: string
  scheduleDescription?: string
  reminderList?: ReminderItem[]
}

const getFormSchema = (taskScheduleSettingType: TaskScheduleSettingType): Joi.ObjectSchema<FormData> => {
  const basic: Joi.SchemaMap<FormData> = {
    name: v.string().max(50).required(),
    description: v.string().max(250).required(),
    reminderList: v.array().optional(),
  }
  if (taskScheduleSettingType === TaskScheduleSettingType.Legacy) {
    return v.object<FormData>({
      ...basic,
      scheduleRepeat: v.string().optional(),
      scheduleDescription: v.string().max(50).required(),
    })
  } else {
    return v.object<FormData>(basic)
  }
}

interface TaskExampleContainerProps {
  children: React.ReactNode
  index?: number
  style?: React.CSSProperties
}

const TaskExampleContainer = (props: TaskExampleContainerProps) => {
  const {index, children, style} = props
  const {color, fontSize, fontWeight} = selectTheme()

  const exampleString = index ? `Example ${index}` : 'Example'

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        height: 'fit-content',
        marginBottom: '20px',
        ...style,
      }}>
      <p
        css={{
          fontSize: fontSize.h7,
          fontWeight: fontWeight.bold,
          color: color.tag.yellow.dark,
        }}>
        {exampleString}
      </p>
      <div
        css={{
          display: 'flex',
          gap: '8px',
          alignItems: 'stretch',
        }}>
        <div
          css={{
            width: '4px',
            backgroundColor: color.border.default,
            flexShrink: 0,
          }}
        />
        {children}
      </div>
    </div>
  )
}

const taskExampleContentBuilder = (
  title: string,
  ...points: {title: string; description: string}[]
): React.ReactNode => {
  const {color, fontSize, fontWeight} = selectTheme()
  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}>
      <p
        css={{
          fontSize: fontSize.h6,
          fontWeight: fontWeight.thick,
          color: color.textIcon.title,
        }}>
        {title}
      </p>
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}>
        {points.map(({title, description}, index) => (
          <div
            key={index}
            css={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '4px',
            }}>
            <p
              css={{
                fontSize: fontSize.h7,
                fontWeight: fontWeight.thick,
                color: color.textIcon.title,
              }}>
              •
            </p>
            <p>
              <span
                css={{
                  fontSize: fontSize.h7,
                  fontWeight: fontWeight.thick,
                  color: color.textIcon.title,
                }}>
                {title}:{' '}
              </span>
              <span
                css={{
                  fontSize: fontSize.h7,
                  fontWeight: fontWeight.regular,
                  color: color.textIcon.title,
                }}>
                {description}
              </span>
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

export interface AddTaskPageProps {
  isEditing: EditingTask | null
  taskType: TaskType
  closePopup: () => void
  setDisplayChangePlanPage: Dispatch<SetStateAction<boolean>>
  archiveTask: (task: TempTask) => void
  unarchiveTask: (task: TempTask) => void
  taskScheduleSettingType: TaskScheduleSettingType
  projectScheduleConfig?: IProjectScheduleConfig
}

export const AddTaskPage = (props: AddTaskPageProps) => {
  const {color, fontSize, fontWeight, pad} = selectTheme()
  const colorTranslator = createTaskColorTranslator()

  const {
    doREQUEST_TASK_DELETE,
    doREQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE,
    doREQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_UPDATE,
    doREQUEST_TASK_TODO_CREATE,
    doREQUEST_TASK_TODO_UPDATE,
    doREQUEST_TASK_TIMER_CREATE,
    doREQUEST_TASK_TIMER_UPDATE,
    doREQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_CREATE,
    doREQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_UPDATE,
    doREQUEST_TASK_STOPWATCH_GARMIN_STREAM_CREATE,
    doREQUEST_TASK_STOPWATCH_GARMIN_STREAM_UPDATE,
    doREQUEST_TASK_UPDATE,
    doTEMP_TASK_DATA_UPDATE_TASK,
    doTEMP_TASK_DATA_ADD_TASK,
    doTEMP_TASK_DATA_DELETE_TASK,
    doREQUEST_TASK_SCHEDULE_CREATE,
    doREQUEST_TASK_SCHEDULE_UPDATE,
    doREQUEST_TASK_SCHEDULE_DELETE,
    doREQUEST_TASK_SCHEDULE_REMINDER_UPDATE,
    doREQUEST_TASK_EVENT_TRACKER_CREATE,
    doREQUEST_WORKSPACE_ALL_QUESTIONNAIRE_FETCH,
    doREQUEST_PROJECT_LIST_FETCH,
  } = createDispatchActions()

  const {
    isEditing,
    taskType,
    closePopup,
    setDisplayChangePlanPage,
    archiveTask,
    unarchiveTask,
    taskScheduleSettingType,
  } = props

  /* ------------------ form ------------------ */
  const {register, control, handleSubmit, watch, formState, trigger} = useForm<FormData>({
    schema: getFormSchema(taskScheduleSettingType),
    mode: 'onChange',
  })

  const nameField = watch('name')
  const descriptionField = watch('description')
  const scheduleDescriptionField = watch('scheduleDescription')
  const scheduleRepeatField = watch('scheduleRepeat')
  const reminderListField = watch('reminderList')

  /* ------------------ basic state ------------------ */
  const {projectId, project} = useCurrentProjectState()
  const projectData = selectProjectData()
  const methodState = selectMethod()
  const methodId = methodState?.id ?? undefined
  const existedTaskIdList = methodState?.taskList?.map((task) => task.id) ?? []
  const isEditingLiveProject = project?.status === 'live'
  const tempTaskData = selectTempTaskData()
  const tempTaskList = tempTaskData?.taskList?.filter((item: TempTask) => item.type !== TaskType.GarminDevice) || []

  const {workspaceId} = useCurrentWorkspaceState()
  const collaboratorType = useCollaboratorTypeInProject()
  const hasCreatePermission = collaboratorTypeHasRole({
    collaboratorType,
    projectRole: ProjectCollaboratorRole.TaskCreate,
    workspaceRole: WorkspaceCollaboratorRole.TaskCreate,
  })
  const hasDeletePermission = collaboratorTypeHasRole({
    collaboratorType,
    projectRole: ProjectCollaboratorRole.TaskDelete,
    workspaceRole: WorkspaceCollaboratorRole.TaskDelete,
  })

  const [taskColor, setTaskColor] = useState<string>(isEditing?.id ? `#${isEditing.color}` : colorTranslator(0))

  const [showPopupConfirmDeleteTask, setShowPopupConfirmDeleteTask] = useState(false)
  const [showPopupConfirmDiscardTask, setShowPopupConfirmDiscardTask] = useState(false)
  const [showPopupConfirmShowTask, setShowPopupConfirmShowTask] = useState(false)

  const [showAddTaskBackIntoAppTooltip, setShowAddTaskBackIntoAppTooltip] = useState(false)
  const [showRemoveButtonPopup, setShowRemoveButtonPopup] = useState(false)
  const [taskScheduleReadonly, setTaskScheduleReadonly] = useState(false)

  // QN
  const [renderDuplicateExistingQuestionnairePopup, setRenderDuplicateExistingQuestionnairePopup] = useState(false)
  const [renderLiveEditQuestionnaireTooltip, setRenderLiveEditQuestionnaireTooltip] = useState(false)
  const [questionnaire, setQuestionnaire] = useState<TempQuestionnaire | IQuestionnaire | null>(null)
  const [requestWorkspaceAllQuestionnaireFetchResult, setRequestWorkspaceAllQuestionnaireFetchResult] =
    useState<RequestResult | null>(null)
  const [renderQuestionnairePage, setRenderQuestionnairePage] = useState(false)
  const isTaskPendingToBeCreated = !isEditing || (isEditing && !existedTaskIdList.includes(isEditing.id))
  const [fetchingQuestionnaires, setFetchingQuestionnaires] = useState(false)
  const [questionnaireList, setQuestionnaireList] = useState<IQuestionnaire[]>([])
  const [upsertDisplayStrategyList, setUpsertDisplayStrategyList] = useState<
    TempQuestionnaireDisplayStrategyUpsertItem[]
  >([])

  // initialize display strategy upsert list from backend data
  useMemo(() => {
    if (!questionnaire) return
    if (questionnaire.sectionList.length === 0) return
    if (upsertDisplayStrategyList.length > 0) return
    setUpsertDisplayStrategyList(fetchDisplayStrategyListFromQuestionnaire(questionnaire))
  }, [questionnaire])

  const deleteQuestionnaire = () => {
    setUpsertDisplayStrategyList([])
    setQuestionnaire(null)
  }

  const fetchAllQuestionnaire = () => {
    if (Object.keys(projectData).length <= 1) {
      doREQUEST_PROJECT_LIST_FETCH({
        payload: {
          page: 0,
        },
      })
    }
    setFetchingQuestionnaires(true)
    doREQUEST_WORKSPACE_ALL_QUESTIONNAIRE_FETCH({
      payload: {
        workspaceId,
      },
      setRequestResult: setRequestWorkspaceAllQuestionnaireFetchResult,
    })
  }

  useEffect(() => {
    if (isEditing?.questionnaire) {
      setQuestionnaire(isEditing.questionnaire)
    }
  }, [isEditing])

  useEffect(() => {
    if (!fetchingQuestionnaires) return
    if (!requestWorkspaceAllQuestionnaireFetchResult?.success) {
      alert('Failed to fetch questionnaires')
    } else if (requestWorkspaceAllQuestionnaireFetchResult?.success) {
      setQuestionnaireList(requestWorkspaceAllQuestionnaireFetchResult.result.payload)
      setRenderDuplicateExistingQuestionnairePopup(true)
    }
    setFetchingQuestionnaires(false)
  }, [requestWorkspaceAllQuestionnaireFetchResult])

  // task api
  const [requestCreateTaskResult, setRequestCreateTaskResult] = useState<RequestResult | null>(null)
  const [requestUpdateTaskResult, setRequestUpdateTaskResult] = useState<RequestResult | null>(null)

  // task schedule
  const [taskScheduleQueue, taskScheduleQueueActions] = useList<ActionableTempTaskSchedule>([])
  const [taskScheduleList, taskScheduleListActions] = useList<ActionableTempTaskSchedule>([])

  // form state
  const [nameLength, setNameLength] = useState(0)
  const [descriptionLength, setDescriptionLength] = useState(0)
  const [scheduleDescriptionLength, setScheduleDescriptionLength] = useState(0)
  const formHasData =
    nameLength > 0 || descriptionLength > 0 || scheduleDescriptionLength > 0 || taskScheduleQueue.length > 0

  useUpdateEffect(() => {
    trigger(undefined, {shouldFocus: false})
  }, [taskScheduleQueue])

  const taskInLiveEditing = useMemo(() => {
    return isEditingLiveProject && isEditing ? (isEditing as TempTask) : undefined
  }, [isEditing])

  const [liveEditingScheduleQueue, setLiveEditingScheduleQueue] = useState<ActionableTempTaskSchedule[]>(
    taskInLiveEditing?.liveEditingScheduleQueue ?? [],
  )

  useEffect(() => {
    if (taskInLiveEditing?.type !== TaskType.Questionnaire) return
    if (taskInLiveEditing.questionnaire && taskInLiveEditing.displayStrategyList) {
      setUpsertDisplayStrategyList(taskInLiveEditing.displayStrategyList)
    }
  }, [taskInLiveEditing])

  const disableLiveEdit = taskInLiveEditing ? !taskInLiveEditing?.enabled : false

  const existingSchedules: ActionableTempTaskSchedule[] = useMemo(() => {
    return (isEditing?.scheduleList ?? [])
      .filter((schedule) => schedule.current)
      .map((schedule: ITaskSchedule) => {
        return {
          tempId: schedule.id,
          createdAt: new Date(schedule.createdAt),
          values: schedule,
          action: null,
        }
      })
  }, [isEditing])

  useEffect(() => {
    // live edited scheudles into existingSchedules
    const currentSchedules = [...existingSchedules]
    if (liveEditingScheduleQueue) {
      for (const tempSchedule of liveEditingScheduleQueue) {
        switch (tempSchedule.action) {
          case TempTaskScheduleActionType.Create: {
            currentSchedules.push(tempSchedule)
            break
          }
          case TempTaskScheduleActionType.Delete: {
            const existingScheduleIndex = existingSchedules.findIndex(
              (existing) => existing.tempId === tempSchedule.tempId,
            )
            if (existingScheduleIndex !== -1) {
              currentSchedules.splice(existingScheduleIndex, 1)
            }
            break
          }
          case TempTaskScheduleActionType.Update: {
            const existingScheduleIndex = existingSchedules.findIndex(
              (existing) => existing.tempId === tempSchedule.tempId,
            )
            if (existingScheduleIndex !== -1) {
              currentSchedules[existingScheduleIndex] = tempSchedule
            }
            break
          }
          case TempTaskScheduleActionType.UpdateReminderList: {
            const existingScheduleIndex = existingSchedules.findIndex(
              (existing) => existing.tempId === tempSchedule.tempId,
            )
            if (existingScheduleIndex !== -1) {
              currentSchedules[existingScheduleIndex] = tempSchedule
            }
            break
          }
        }
      }
    }

    for (const queuedSchedule of taskScheduleQueue) {
      switch (queuedSchedule.action) {
        case TempTaskScheduleActionType.Create: {
          currentSchedules.push(queuedSchedule)
          break
        }
        case TempTaskScheduleActionType.Delete: {
          const existingScheduleIndex = currentSchedules.findIndex(
            (existing) => existing.tempId === queuedSchedule.tempId,
          )
          if (existingScheduleIndex !== -1) {
            currentSchedules.splice(existingScheduleIndex, 1)
          }
          break
        }
        case TempTaskScheduleActionType.Update: {
          const existingScheduleIndex = currentSchedules.findIndex(
            (existing) => existing.tempId === queuedSchedule.tempId,
          )
          if (existingScheduleIndex !== -1) {
            currentSchedules[existingScheduleIndex] = queuedSchedule
          }
          break
        }
        case TempTaskScheduleActionType.UpdateReminderList: {
          const existingScheduleIndex = currentSchedules.findIndex(
            (existing) => existing.tempId === queuedSchedule.tempId,
          )
          if (existingScheduleIndex !== -1) {
            currentSchedules[existingScheduleIndex] = queuedSchedule
          }
          break
        }
      }
    }
    taskScheduleListActions.set(currentSchedules)
  }, [existingSchedules, taskScheduleQueue, liveEditingScheduleQueue])

  useEffect(() => {
    if (!requestCreateTaskResult?.success) return
    const taskId = requestCreateTaskResult.result.payload.id

    switch (taskScheduleSettingType) {
      case TaskScheduleSettingType.Schedule: {
        executeTaskScheduleRequest(taskId, taskScheduleQueue)
        return
      }
      case TaskScheduleSettingType.EventTracker: {
        doREQUEST_TASK_EVENT_TRACKER_CREATE({
          payload: {
            eventTracker: {
              taskId,
            },
          },
        })
        break
      }
      default:
        break
    }

    closePopupAction()
  }, [requestCreateTaskResult])

  const [lastScheduleRelatedRequestResult, setLastScheduleRelatedRequestResult] = useState<RequestResult | null>(null)
  const [scheduleRequestTaskId, setScheduleRequestTaskId] = useState<string | null>(null)
  const [pendingScheduleActionList, setPendingScheduleActionList] = useState<ActionableTempTaskSchedule[]>([])
  const [currentScheduleAction, setCurrentScheduleAction] = useState<ActionableTempTaskSchedule | null>(null)
  const [requestCreateTaskScheduleResult, setRequestCreateTaskScheduleResult] = useState<RequestResult | null>(null)
  const [requestUpdateTaskScheduleResult, setRequestUpdateTaskScheduleResult] = useState<RequestResult | null>(null)
  const [requestDeleteTaskScheduleResult, setRequestDeleteTaskScheduleResult] = useState<RequestResult | null>(null)
  const [requestUpdateTaskScheduleReminderResult, setRequestUpdateTaskScheduleReminderResult] =
    useState<RequestResult | null>(null)

  const resolveTaskScheduleIdFromRequestResult = (
    requestResult: RequestResult | null,
    fallbackId?: string,
  ): string | undefined => {
    const payload = requestResult?.result?.payload
    return payload?.replaced?.id ?? payload?.original?.id ?? payload?.id ?? fallbackId
  }

  const resetTaskScheduleRequestResults = () => {
    setRequestCreateTaskScheduleResult(null)
    setRequestUpdateTaskScheduleResult(null)
    setRequestDeleteTaskScheduleResult(null)
    setRequestUpdateTaskScheduleReminderResult(null)
  }

  const finishCurrentScheduleRequest = (requestResult: RequestResult | null) => {
    if (requestResult && !requestResult.success) {
      setLastScheduleRelatedRequestResult(requestResult)
      return
    }

    if (pendingScheduleActionList.length === 0) {
      setCurrentScheduleAction(null)
      setScheduleRequestTaskId(null)
      if (requestResult) {
        setLastScheduleRelatedRequestResult(requestResult)
      } else {
        closePopupAction()
      }
      return
    }

    setCurrentScheduleAction(null)
  }

  const requestTaskScheduleReminderUpdate = (taskScheduleId: string, scheduleAction: ActionableTempTaskSchedule) => {
    setRequestUpdateTaskScheduleReminderResult(null)
    doREQUEST_TASK_SCHEDULE_REMINDER_UPDATE({
      setRequestResult: setRequestUpdateTaskScheduleReminderResult,
      payload: {
        taskScheduleId,
        reminderList: normalizeTaskScheduleReminderList(
          scheduleAction.values.reminderList as TaskScheduleReminderDTO[] | undefined,
        ),
      },
    })
  }

  const executeTaskScheduleRequest = (taskId: string, actions: ActionableTempTaskSchedule[]) => {
    const sortedActions = actions
      .filter((taskScheduleAction) => taskScheduleAction.action)
      .sort((a, b) => {
        if (a.action === TempTaskScheduleActionType.Delete && b.action !== TempTaskScheduleActionType.Delete) return 1
        if (a.action !== TempTaskScheduleActionType.Delete && b.action === TempTaskScheduleActionType.Delete) return -1
        return 0
      })

    if (sortedActions.length === 0) {
      closePopupAction()
      return
    }

    resetTaskScheduleRequestResults()
    setLastScheduleRelatedRequestResult(null)
    setScheduleRequestTaskId(taskId)
    setPendingScheduleActionList(sortedActions)
    setCurrentScheduleAction(null)
  }

  useEffect(() => {
    if (!scheduleRequestTaskId) return
    if (currentScheduleAction) return
    if (pendingScheduleActionList.length === 0) return

    const [nextScheduleAction, ...remainingScheduleActions] = pendingScheduleActionList
    setCurrentScheduleAction(nextScheduleAction)
    setPendingScheduleActionList(remainingScheduleActions)
  }, [scheduleRequestTaskId, currentScheduleAction, pendingScheduleActionList])

  useEffect(() => {
    if (!scheduleRequestTaskId || !currentScheduleAction) return

    const {action, values} = currentScheduleAction
    switch (action) {
      case TempTaskScheduleActionType.Create: {
        setRequestCreateTaskScheduleResult(null)
        doREQUEST_TASK_SCHEDULE_CREATE({
          setRequestResult: setRequestCreateTaskScheduleResult,
          payload: {
            schedule: {
              ...values,
              taskId: scheduleRequestTaskId,
            },
          },
        })
        break
      }

      case TempTaskScheduleActionType.Update: {
        setRequestUpdateTaskScheduleResult(null)
        doREQUEST_TASK_SCHEDULE_UPDATE({
          setRequestResult: setRequestUpdateTaskScheduleResult,
          payload: {
            schedule: values,
          },
        })
        break
      }

      case TempTaskScheduleActionType.UpdateReminderList: {
        if (!values.id) {
          finishCurrentScheduleRequest(null)
          break
        }

        requestTaskScheduleReminderUpdate(values.id, currentScheduleAction)
        break
      }

      case TempTaskScheduleActionType.Delete: {
        setRequestDeleteTaskScheduleResult(null)
        doREQUEST_TASK_SCHEDULE_DELETE({
          setRequestResult: setRequestDeleteTaskScheduleResult,
          payload: {
            id: values.id,
          },
        })
        break
      }
    }
  }, [scheduleRequestTaskId, currentScheduleAction])

  useEffect(() => {
    if (!requestCreateTaskScheduleResult) return
    if (currentScheduleAction?.action !== TempTaskScheduleActionType.Create) return

    if (!requestCreateTaskScheduleResult.success) {
      finishCurrentScheduleRequest(requestCreateTaskScheduleResult)
      return
    }

    const taskScheduleId = resolveTaskScheduleIdFromRequestResult(
      requestCreateTaskScheduleResult,
      currentScheduleAction.values.id,
    )
    if (taskScheduleId && currentScheduleAction.reminderListChanged) {
      requestTaskScheduleReminderUpdate(taskScheduleId, currentScheduleAction)
      return
    }

    finishCurrentScheduleRequest(requestCreateTaskScheduleResult)
  }, [requestCreateTaskScheduleResult])

  useEffect(() => {
    if (!requestUpdateTaskScheduleResult) return
    if (currentScheduleAction?.action !== TempTaskScheduleActionType.Update) return

    if (!requestUpdateTaskScheduleResult.success) {
      finishCurrentScheduleRequest(requestUpdateTaskScheduleResult)
      return
    }

    const taskScheduleId = resolveTaskScheduleIdFromRequestResult(
      requestUpdateTaskScheduleResult,
      currentScheduleAction.values.id,
    )
    if (taskScheduleId && currentScheduleAction.reminderListChanged) {
      requestTaskScheduleReminderUpdate(taskScheduleId, currentScheduleAction)
      return
    }

    finishCurrentScheduleRequest(requestUpdateTaskScheduleResult)
  }, [requestUpdateTaskScheduleResult])

  useEffect(() => {
    if (!requestDeleteTaskScheduleResult) return
    if (currentScheduleAction?.action !== TempTaskScheduleActionType.Delete) return

    finishCurrentScheduleRequest(requestDeleteTaskScheduleResult)
  }, [requestDeleteTaskScheduleResult])

  useEffect(() => {
    if (!requestUpdateTaskScheduleReminderResult) return
    if (!currentScheduleAction) return

    finishCurrentScheduleRequest(requestUpdateTaskScheduleReminderResult)
  }, [requestUpdateTaskScheduleReminderResult])

  useEffect(() => {
    if (!requestUpdateTaskResult?.success) return
    const taskId = requestUpdateTaskResult.result.payload.id

    switch (taskScheduleSettingType) {
      case TaskScheduleSettingType.Schedule: {
        executeTaskScheduleRequest(taskId, taskScheduleQueue)
        break
      }
      case TaskScheduleSettingType.EventTracker: {
        if (!isNil(isEditing)) {
          if (isNil(isEditing.eventTracker)) {
            doREQUEST_TASK_EVENT_TRACKER_CREATE({
              setRequestResult: setLastScheduleRelatedRequestResult,
              payload: {
                eventTracker: {
                  taskId,
                },
              },
            })
          } else {
            // Add update action if needed. right now task event tracker has no update action
            closePopupAction()
          }
        } else {
          closePopupAction()
        }
        break
      }
      default: {
        closePopupAction()
        break
      }
    }
  }, [requestUpdateTaskResult])

  // close popup after task schedule requests completed
  useEffect(() => {
    if (!lastScheduleRelatedRequestResult?.success) return
    closePopupAction()
  }, [lastScheduleRelatedRequestResult])

  const handleTaskScheduleCreate = (schedule: TempTaskSchedule) => {
    taskScheduleQueueActions.push({
      ...schedule,
      action: TempTaskScheduleActionType.Create,
      reminderListChanged: !!schedule.values.reminderList?.length,
    })
  }

  // update the scheudle which is just in temp state, return flag if it's just a temp schedule
  const updateLiveEditingScheudle = (tempSchedule: TempTaskSchedule & Partial<ActionableTempTaskSchedule>): boolean => {
    // replace live editing schedule
    if (liveEditingScheduleQueue) {
      const scheduleIndex = liveEditingScheduleQueue.findIndex((schedule) => schedule.tempId == tempSchedule.tempId)
      if (scheduleIndex != -1) {
        const existingSchedule = liveEditingScheduleQueue[scheduleIndex]
        liveEditingScheduleQueue[scheduleIndex] = {
          ...existingSchedule,
          values: tempSchedule.values,
          reminderListChanged: tempSchedule.reminderListChanged ?? existingSchedule.reminderListChanged,
        }
        setLiveEditingScheduleQueue([...liveEditingScheduleQueue])
        return true
      }
    }
    return false
  }

  const handleTaskScheduleUpdate = (schedule: TempTaskSchedule) => {
    const existingQueuedSchedule = taskScheduleQueue.find((existing) => existing.tempId === schedule.tempId)
    if (schedule.values.id) {
      taskScheduleQueueActions.upsert((existing) => existing.tempId === schedule.tempId, {
        ...schedule,
        action: TempTaskScheduleActionType.Update,
        reminderListChanged: existingQueuedSchedule?.reminderListChanged,
      })
    } else {
      const liveEditingScheudleUpdated = updateLiveEditingScheudle(schedule)
      if (!liveEditingScheudleUpdated) {
        taskScheduleQueueActions.update((existing) => existing.tempId === schedule.tempId, {
          ...schedule,
          action: existingQueuedSchedule?.action ?? TempTaskScheduleActionType.Create,
          reminderListChanged: existingQueuedSchedule?.reminderListChanged,
        })
      }
    }
  }

  const handleTaskScheduleReminderListUpdate = (schedule: TempTaskSchedule) => {
    const existingQueuedSchedule = taskScheduleQueue.find((existing) => existing.tempId === schedule.tempId)

    if (schedule.values.id) {
      const action =
        existingQueuedSchedule?.action === TempTaskScheduleActionType.Update ||
        existingQueuedSchedule?.action === TempTaskScheduleActionType.Create
          ? existingQueuedSchedule.action
          : TempTaskScheduleActionType.UpdateReminderList

      taskScheduleQueueActions.upsert((existing) => existing.tempId === schedule.tempId, {
        ...existingQueuedSchedule,
        ...schedule,
        values: {
          ...existingQueuedSchedule?.values,
          ...schedule.values,
        },
        action,
        reminderListChanged: true,
      })
      return
    }

    const liveEditingScheudleUpdated = updateLiveEditingScheudle({
      ...schedule,
      reminderListChanged: true,
    })
    if (liveEditingScheudleUpdated) return

    taskScheduleQueueActions.update((existing) => existing.tempId === schedule.tempId, {
      ...existingQueuedSchedule,
      ...schedule,
      values: {
        ...existingQueuedSchedule?.values,
        ...schedule.values,
      },
      action: existingQueuedSchedule?.action ?? TempTaskScheduleActionType.Create,
      reminderListChanged: true,
    })
  }

  // remove the scheudle which is just in temp state, return flag if it's just a temp schedule
  const removeLiveEditingScheudle = (tempSchedule: TempTaskSchedule): boolean => {
    if (taskInLiveEditing) {
      // remove live editing schedule
      if (liveEditingScheduleQueue) {
        const scheduleIndex = liveEditingScheduleQueue.findIndex((schedule) => schedule.tempId == tempSchedule.tempId)
        if (scheduleIndex != -1) {
          liveEditingScheduleQueue.splice(scheduleIndex, 1)
          setLiveEditingScheduleQueue([...liveEditingScheduleQueue])
          return true
        }
      }
    }
    return false
  }

  const handleTaskScheduleDelete = (schedule: TempTaskSchedule) => {
    if (schedule.values.id) {
      // delete exsiting schedule
      taskScheduleQueueActions.upsert((existing) => existing.tempId === schedule.tempId, {
        ...schedule,
        action: TempTaskScheduleActionType.Delete,
      })
    } else {
      // delete live editing schedule
      const liveEditingScheduleRemoved = removeLiveEditingScheudle(schedule)
      if (!liveEditingScheduleRemoved) {
        taskScheduleQueueActions.filter((existing) => existing.tempId !== schedule.tempId)
      }
    }
  }

  // movesense
  const movesenseStreamTask: ITask | undefined = _.find(methodState?.taskList, {
    id: isEditing?.id,
  })
  const movesenseStreamTaskDetail: IStopwatchMovesenseStream | undefined = movesenseStreamTask?.stopwatchMovesenseStream
  const tempMovesenseStreamTask: TempTask | undefined = _.find(tempTaskData?.taskList, {
    id: isEditing?.id,
  })
  const tempMovesenseStreamTaskDetail: TempStopwatchMovesenseStream | undefined =
    tempMovesenseStreamTask?.stopwatchMovesenseStream

  const parseMovesenseStreamSpec = (
    taskDetail: TempStopwatchMovesenseStream | undefined,
  ): tempStopwatchMovesenseStreamSpec => {
    return {
      imuEnable: taskDetail?.imuEnable ?? false,
      imuHz: taskDetail?.imuHz ?? 13,
      accEnable: taskDetail?.accEnable ?? false,
      accHz: taskDetail?.accHz ?? 13,
      gyroscopeEnable: taskDetail?.gyroscopeEnable ?? false,
      gyroscopeHz: taskDetail?.gyroscopeHz ?? 13,
      magnetometerEnable: taskDetail?.magnetometerEnable ?? false,
      magnetometerHz: taskDetail?.magnetometerHz ?? 13,
      ecgEnable: taskDetail?.ecgEnable ?? false,
      ecgHz: taskDetail?.ecgHz ?? 125,
      rrEnable: taskDetail?.rrEnable ?? false,
      timeLimitSeconds: taskDetail?.timeLimitSeconds ?? 300,
    }
  }

  const [movesenseStreamEnable, setMovesenseStreamEnable] = useState(true)
  const [movesenseStreamTaskCopy, setMovesenseStreamTaskCopy] = useState<tempStopwatchMovesenseStreamSpec>(
    isEditingLiveProject
      ? parseMovesenseStreamSpec(tempMovesenseStreamTaskDetail)
      : parseMovesenseStreamSpec(movesenseStreamTaskDetail),
  )
  const [movesenseReadOnly, setMovesenseReadOnly] = useState(false)

  // garmin stream
  const garminStreamTask: ITask | undefined = _.find(methodState?.taskList, {
    id: isEditing?.id,
  })
  const garminStreamTaskDetail: IStopwatchGarminStream | undefined | null = garminStreamTask?.stopwatchGarminStream
  const tempGarminStreamTask: TempTask | undefined = _.find(tempTaskData?.taskList, {
    id: isEditing?.id,
  })
  const tempGarminStreamTaskDetail: TempStopwatchGarminStream | undefined | null =
    tempGarminStreamTask?.stopwatchGarminStream

  const parseGarminStreamSpec = (
    taskDetail: TempStopwatchGarminStream | undefined | null,
  ): tempStopwatchGarminStreamSpec => {
    return {
      accEnable: taskDetail?.accEnable ?? true,
      accHz: taskDetail?.accHz ?? 25,
      timeLimitSeconds: taskDetail?.timeLimitSeconds ?? 300,
    }
  }

  const [garminStreamEnable, setGarminStreamEnable] = useState(true)
  const [garminStreamTaskCopy, setGarminStreamTaskCopy] = useState<tempStopwatchGarminStreamSpec>(
    isEditingLiveProject
      ? parseGarminStreamSpec(tempGarminStreamTaskDetail)
      : parseGarminStreamSpec(garminStreamTaskDetail),
  )
  const [garminStreamReadOnly, setGarminStreamReadOnly] = useState(false)

  /* ------------------ default effect ------------------ */
  const isEditingLiveTask = isEditingLiveProject && (methodState.taskList ?? []).some((t) => t.id === isEditing?.id)
  useEffect(() => {
    if (hasCreatePermission && !isEditingLiveTask) {
      setTaskScheduleReadonly(false)
      setMovesenseReadOnly(false)
      setGarminStreamReadOnly(false)
      return
    }
    setTaskScheduleReadonly(true)
    setMovesenseReadOnly(true)
    setGarminStreamReadOnly(true)
  }, [isEditing])

  // input
  useEffect(() => {
    if (nameField) {
      setNameLength(nameField.length)
    }
    if (descriptionField) {
      setDescriptionLength(descriptionField.length)
    }
    if (scheduleDescriptionField) {
      setScheduleDescriptionLength(scheduleDescriptionField.length)
    }
  }, [nameField, descriptionField, scheduleDescriptionField])

  const closePopupAction = () => {
    if (!closePopup) return
    closePopup()
    // setAutoAddTaskType('')
    // if (taskType === TaskType.Questionnaire) doQUESTIONNAIRES_DELETE({id: 'questionnaireDraft'})
  }

  const createQuestionnaireDraftFromOtherQuestionnaire = (questionnaire: IQuestionnaire) => {
    setQuestionnaire({
      taskId: isEditing?.id ?? 'taskDraft',
      projectId: projectId ?? '',
      workspaceId: workspaceId ?? '',
      name: questionnaire.name,
      description: questionnaire.description,
      sectionList: questionnaire.sectionList.map(sanitizeDuplicatedSection),
    })
    setRenderQuestionnairePage(true)
  }

  const createQuestionnaireDraft = () => {
    setQuestionnaire({
      taskId: isEditing?.id ?? 'taskDraft',
      projectId: projectId ?? '',
      workspaceId: workspaceId ?? '',
      name: nameField ? nameField : 'Untitled Questionnaire',
      description: descriptionField ? descriptionField : 'Description goes here',
      sectionList: [
        {
          id: t.uuid(),
          legend: '',
          index: 0,
          description: '',
          questionList: [],
        },
      ],
    })
    setRenderQuestionnairePage(true)
  }

  const editQuestionnaireButtonEnabled = useMemo(() => {
    if (questionnaire) {
      if (isEditingLiveProject) {
        return isTaskPendingToBeCreated
      }
      return true
    }
    return false
  }, [isEditingLiveProject, isTaskPendingToBeCreated, questionnaire])

  const editQuestionnaire = () => {
    if (editQuestionnaireButtonEnabled) {
      setRenderQuestionnairePage(true)
    }
  }

  // // remove unnecessary properties from question
  // const sanitizeTempQuestion = (question: IQuestionnaireQuestion | QuestionnaireTempQuestion) => {
  //   return omit(question, ['id'])
  // }

  /* ------------------ Movesense effect ------------------ */
  useEffect(() => {
    const result =
      movesenseStreamTaskCopy.imuEnable ||
      movesenseStreamTaskCopy.accEnable ||
      movesenseStreamTaskCopy.gyroscopeEnable ||
      movesenseStreamTaskCopy.magnetometerEnable ||
      movesenseStreamTaskCopy.ecgEnable ||
      movesenseStreamTaskCopy.rrEnable
    setMovesenseStreamEnable(result ?? false)
  }, [movesenseStreamTaskCopy])

  /* ------------------ garmin stream effect ------------------ */
  useEffect(() => {
    setGarminStreamEnable(garminStreamTaskCopy.accEnable ?? false)
  }, [garminStreamTaskCopy])

  /* ------------------ submit (TempTask create/update) ------------------ */
  const onSubmit: SubmitHandler<FormData> = (data) => {
    const isExistingTask = isEditing?.id != undefined
    if (isEditingLiveProject) {
      if (isExistingTask) {
        saveTempTaskToBeUpdated(data)
      } else {
        saveTempTaskToBeCreated(data)
      }
    } else {
      if (isExistingTask) {
        updateTask(data)
      } else {
        createTask(data)
      }
    }
  }

  const onError = (error: unknown) => {
    console.error('error', error)
  }

  /* ------------------------ request (Task, TempTask CRUD ) ------------------------ */
  const createTask = (data: FormData) => {
    const newTask = {
      name: data.name,
      methodId,
      scheduleRepeat: data.scheduleRepeat === 'repeat',
      scheduleDescription: data.scheduleDescription,
      reminderList: data.reminderList,
      description: data.description || ' ',
      color: taskColor.substring(1),
    }
    switch (taskType) {
      case TaskType.Todo:
        doREQUEST_TASK_TODO_CREATE({
          setRequestResult: setRequestCreateTaskResult,
          payload: newTask,
        })
        break

      case TaskType.Timer:
        doREQUEST_TASK_TIMER_CREATE({
          setRequestResult: setRequestCreateTaskResult,
          payload: newTask,
        })
        break

      case TaskType.Questionnaire: {
        doREQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_CREATE({
          setRequestResult: setRequestCreateTaskResult,
          payload: {
            ...newTask,
            sectionList: questionnaire?.sectionList,
            displayStrategyList: upsertDisplayStrategyList,
          },
        })
        break
      }
      case TaskType.StopwatchMovesenseStream: {
        doREQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_CREATE({
          setRequestResult: setRequestCreateTaskResult,
          payload: {
            ...newTask,
            ...movesenseStreamTaskCopy,
          },
        })
        break
      }
      case TaskType.StopwatchGarminStream: {
        doREQUEST_TASK_STOPWATCH_GARMIN_STREAM_CREATE({
          setRequestResult: setRequestCreateTaskResult,
          payload: {
            ...newTask,
            ...garminStreamTaskCopy,
          },
        })
        break
      }
    }
    if (taskScheduleSettingType === TaskScheduleSettingType.Legacy) {
      closePopupAction()
    }
  }

  const updateTask = (data: FormData) => {
    switch (taskType) {
      case TaskType.Todo:
        doREQUEST_TASK_TODO_UPDATE({
          setRequestResult: setRequestUpdateTaskResult,
          payload: {
            ...data,
            reminderList: data.reminderList,
            scheduleRepeat: data.scheduleRepeat === 'repeat',
            description: data.description || ' ',
            taskId: isEditing?.id,
            color: taskColor.substring(1),
          },
        })
        break
      case TaskType.Timer:
        doREQUEST_TASK_TIMER_UPDATE({
          setRequestResult: setRequestUpdateTaskResult,
          payload: {
            ...data,
            reminderList: data.reminderList,
            scheduleRepeat: data.scheduleRepeat === 'repeat',
            description: data.description || ' ',
            taskId: isEditing?.id,
            color: taskColor.substring(1),
          },
        })
        break
      case TaskType.Questionnaire: {
        if (!questionnaire) return

        doREQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_UPDATE({
          setRequestResult: setRequestUpdateTaskResult,
          payload: {
            id: isEditing?.id,
            projectId: isEditing?.projectId,
            workspaceId: isEditing?.workspaceId,
            methodId: isEditing?.methodId,
            type: TaskType.Questionnaire,
            scheduleDescription: data.scheduleDescription,
            scheduleRepeat: data.scheduleRepeat === 'repeat',
            reminderList: data.reminderList,
            questionnaire: {
              ...questionnaire,
              name: data.name,
              description: data.description,
            },
            displayStrategyList: upsertDisplayStrategyList,
            color: taskColor.substring(1),
          },
        })
        break
      }
      case TaskType.StopwatchMovesenseStream:
        doREQUEST_TASK_STOPWATCH_MOVESENSE_STREAM_UPDATE({
          setRequestResult: setRequestUpdateTaskResult,
          payload: {
            ...data,
            reminderList: data.reminderList,
            scheduleRepeat: data.scheduleRepeat === 'repeat',
            description: data.description || ' ',
            taskId: isEditing?.id,
            color: taskColor.substring(1),
            ...movesenseStreamTaskCopy,
          },
        })
        break
      case TaskType.StopwatchGarminStream:
        doREQUEST_TASK_STOPWATCH_GARMIN_STREAM_UPDATE({
          setRequestResult: setRequestUpdateTaskResult,
          payload: {
            ...data,
            reminderList: data.reminderList,
            scheduleRepeat: data.scheduleRepeat === 'repeat',
            description: data.description || ' ',
            taskId: isEditing?.id,
            color: taskColor.substring(1),
            ...garminStreamTaskCopy,
          },
        })
        break
    }
    if (taskScheduleSettingType === TaskScheduleSettingType.Legacy) {
      closePopupAction()
    }
  }

  const saveTempTaskToBeCreated = (data: FormData) => {
    const tempTaskId = t.uuid()

    const liveEditingScheduleQueue =
      taskScheduleSettingType == TaskScheduleSettingType.Schedule ? taskScheduleQueue : undefined

    const eventTracker =
      taskScheduleSettingType == TaskScheduleSettingType.EventTracker
        ? {
            taskId: tempTaskId,
          }
        : undefined

    const tempTask: DeepPartial<TempTask> = {
      id: tempTaskId,
      workspaceId,
      projectId,
      methodId,
      actionType: 'create',
      scheduleRepeat: data.scheduleRepeat === 'repeat',
      scheduleDescription: data.scheduleDescription,
      liveEditingScheduleQueue,
      eventTracker,
      reminderList: data.reminderList,
      color: taskColor.substring(1),
      enabled: true,
      type: taskType,
    }
    const basicChildTaskContent = {
      name: data.name,
      description: data.description,
      projectId,
      taskId: tempTaskId,
    }
    switch (taskType) {
      case TaskType.Todo:
        tempTask.todo = {...basicChildTaskContent}
        break
      case TaskType.Timer:
        tempTask.timer = {...basicChildTaskContent}
        break
      case TaskType.Questionnaire: {
        const newQuestionnaire = {
          ...questionnaire,
          ...basicChildTaskContent,
          name: data.name,
          description: data.description,
        }
        tempTask.questionnaire = newQuestionnaire
        tempTask.displayStrategyList = upsertDisplayStrategyList
        break
      }
      case TaskType.StopwatchMovesenseStream:
        tempTask.stopwatchMovesenseStream = {
          ...basicChildTaskContent,
          ...tempTask.stopwatchMovesenseStream,
          ...movesenseStreamTaskCopy,
        }
        break
      case TaskType.StopwatchGarminStream:
        tempTask.stopwatchGarminStream = {
          ...basicChildTaskContent,
          ...tempTask.stopwatchGarminStream,
          ...garminStreamTaskCopy,
        }
        break
    }
    doTEMP_TASK_DATA_ADD_TASK(tempTask)
    closePopupAction()
  }

  const saveTempTaskToBeUpdated = (data: FormData) => {
    if (!taskInLiveEditing) {
      closePopupAction()
      return
    }

    const newLiveEditingScheduleQueue =
      taskScheduleSettingType == TaskScheduleSettingType.Schedule
        ? liveEditingScheduleQueue
          ? [...liveEditingScheduleQueue, ...taskScheduleQueue]
          : [...taskScheduleQueue]
        : undefined

    const eventTracker =
      taskScheduleSettingType == TaskScheduleSettingType.EventTracker ? taskInLiveEditing.eventTracker : undefined

    const actionType = isEditingLiveTask ? (taskInLiveEditing.actionType == 'show' ? 'show' : 'update') : 'create'

    const tempTask: DeepPartial<TempTask> = {
      id: taskInLiveEditing.id,
      workspaceId,
      projectId,
      methodId,
      actionType,
      index: taskInLiveEditing.index,
      scheduleRepeat: data.scheduleRepeat === 'repeat',
      scheduleDescription: data.scheduleDescription,
      scheduleList: taskInLiveEditing.scheduleList,
      eventTracker,
      liveEditingScheduleQueue: newLiveEditingScheduleQueue,
      reminderList: data.reminderList,
      color: taskColor.substring(1),
      type: taskType,
      enabled: true,
    }
    const basicChildTaskContent = {
      name: data.name,
      description: data.description,
      projectId,
      taskId: isEditing?.id,
    }
    switch (taskType) {
      case TaskType.Todo:
        tempTask.todo = {...basicChildTaskContent}
        break
      case TaskType.Timer:
        tempTask.timer = {...basicChildTaskContent}
        break
      case TaskType.Questionnaire: {
        // const questionnaireWithTempId = insertTempIdIntoQuestionnaire(questionnaire as IQuestionnaire)
        // don't let the old task content in questionnaire overwrite the new ones,
        // basicChildTaskContent must be the last instance
        tempTask.questionnaire = {...questionnaire, ...basicChildTaskContent}
        tempTask.displayStrategyList = upsertDisplayStrategyList
        break
      }
      case TaskType.StopwatchMovesenseStream:
      case TaskType.StopwatchMovesenseLogData:
        tempTask.stopwatchMovesenseStream = {
          ...basicChildTaskContent,
          ...movesenseStreamTaskCopy,
        }
        break
      case TaskType.StopwatchGarminStream:
        tempTask.stopwatchGarminStream = {
          ...basicChildTaskContent,
          ...garminStreamTaskCopy,
        }
        break
    }

    doTEMP_TASK_DATA_UPDATE_TASK(tempTask)
    closePopupAction()
  }

  const deleteTask = () => {
    if (!isEditingLiveProject) {
      doREQUEST_TASK_DELETE({
        payload: {
          taskId: isEditing?.id,
        },
      })
      setShowPopupConfirmDeleteTask(false)
    } else {
      const removingTask = {
        ...tempTaskList.find((task) => task.id === isEditing?.id),
      }
      const isRemovingLiveTask = (methodState.taskList ?? []).some((t) => t.id === isEditing?.id)
      // hide task if it's already exist in backend
      if (isRemovingLiveTask) {
        removingTask.enabled = false
        removingTask.actionType = 'hide'
        doTEMP_TASK_DATA_UPDATE_TASK(removingTask)
      } else {
        // or if not, remove it
        doTEMP_TASK_DATA_DELETE_TASK({
          taskId: isEditing?.id,
        })
      }
    }
    closePopupAction()
  }

  /* ------------------ other effect ------------------ */
  const handleDiscard = () => {
    if (formHasData) {
      setShowPopupConfirmDiscardTask(true)
    } else {
      closePopupAction()
    }
  }

  const handleButtonCancelClick = () => {
    if (isEditing?.id) {
      if (formState.isDirty || taskScheduleQueue.length > 0) {
        setShowPopupConfirmDiscardTask(true)
      } else {
        closePopupAction()
      }
    } else {
      if (formHasData) {
        setShowPopupConfirmDiscardTask(true)
      } else {
        closePopupAction()
      }
    }
  }

  const handleShowHiddenTask = () => {
    if (!isEditingLiveProject) {
      doREQUEST_TASK_UPDATE({
        payload: {
          taskId: isEditing?.id,
          enabled: true,
        },
      })
    } else {
      if (taskInLiveEditing) {
        unarchiveTask(taskInLiveEditing)
      }
    }
    closePopupAction()
  }

  // save Btn disable toggle
  const saveBtnDisabled = useMemo<boolean>(() => {
    let isDisabled: boolean
    if (!formState.isValid) {
      isDisabled = true
    } else if (taskScheduleSettingType === TaskScheduleSettingType.Schedule && !taskScheduleList.length) {
      isDisabled = true
    } else if (taskType === TaskType.Questionnaire) {
      isDisabled = !questionnaire
    } else if (taskType === TaskType.StopwatchMovesenseStream) {
      isDisabled = !movesenseStreamEnable
    } else if (taskType === TaskType.StopwatchGarminStream) {
      isDisabled = !garminStreamEnable
    } else {
      isDisabled = false
    }
    return isDisabled
  }, [formState.isValid, taskScheduleList.length, !!questionnaire, movesenseStreamEnable, garminStreamEnable])

  const taskTypeTranslator: Record<string, string> = {
    todo: 'To-Do Task',
    timer: 'Timer Task',
    questionnaire: 'Questionnaire Task',
    stopwatch_movesense_stream: 'Movesense',
    stopwatch_garmin_stream: 'ACC Streaming Task',
  }

  // handleEsc(handleButtonCancelClick)

  return (
    <>
      {/* page wrapper */}
      <form
        onSubmit={handleSubmit(onSubmit, onError)}
        data-testid='add_task_page'
        css={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          minHeight: '100%',
          // overflowY: 'scroll',
          background: color.background,
          zIndex: 102,
          marginTop: '30px',
          paddingYop: '50px',
        }}>
        {/* ------------------------- save bar ------------------------- */}
        <div
          css={{
            width: '100vw',
            height: '50px',
            boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.13)',
            padding: `0 ${pad.large}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'fixed',
            top: '0',
            left: '0',
            backgroundColor: color.white,
            zIndex: 101,
          }}>
          {/* set cancel button's type to button to prevent form submission */}
          <ButtonCancel data-testid='add_task_page_cancel_btn' type='button' onClick={handleButtonCancelClick} />

          {RIF(
            !isEditing?.id || isEditing?.enabled,
            <div css={{display: 'flex', alignItems: 'center', justifyContent: 'end'}}>
              {RIF(
                isEditing?.id,
                <div
                  onMouseEnter={() => isEditingLiveProject && setShowRemoveButtonPopup(true)}
                  onMouseLeave={() => isEditingLiveProject && setShowRemoveButtonPopup(false)}
                  css={{position: 'relative'}}>
                  {RIF(
                    hasDeletePermission && !isEditingLiveProject,
                    <TextLink
                      type='button'
                      onClick={() => setShowPopupConfirmDeleteTask(true)}
                      css={{color: color.textIcon.warning, marginRight: '20px'}}
                      children={'Delete This Task'}
                    />,
                  )}
                  {RIF(
                    showRemoveButtonPopup,
                    <div
                      css={{
                        position: 'absolute',
                        left: '-80px',
                        top: '32px',
                        width: '221px',
                        borderRadius: '5px',
                        padding: '10px 8px',
                        textAlign: 'center',
                        background: color.black,
                        color: color.white,
                      }}>
                      Remove this task from participant's task list. <br />
                      This action cannot be undone!
                    </div>,
                  )}
                </div>,
              )}
              {RIF(
                hasDeletePermission && taskInLiveEditing,
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'end',
                    position: 'relative',
                  }}>
                  <TextLink
                    type='button'
                    onClick={() => {
                      if (taskInLiveEditing) {
                        archiveTask(taskInLiveEditing)
                      }
                    }}
                    css={{color: color.textIcon.secondary, marginRight: '20px'}}
                    children={'Revert all changes and archive'}
                  />
                  <div css={{width: '1px', height: '32px', background: '#ccc', marginRight: '20px'}}></div>
                </div>,
              )}
              {RIF(
                hasCreatePermission,
                <TextLink
                  type='button'
                  onClick={handleDiscard}
                  css={{color: color.textIcon.secondary, marginRight: '40px'}}
                  children='Discard Changes'
                />,
              )}
              {RIF(
                hasCreatePermission,
                <Button
                  data-testid='add_task_page_save_btn'
                  disabled={saveBtnDisabled}
                  type='submit'
                  btnPadding='medium'>
                  {isEditingLiveProject ? 'Stage changes' : 'Save'}
                </Button>,
              )}
            </div>,
          )}

          {RIF(
            taskInLiveEditing && !taskInLiveEditing.enabled,
            <div css={{display: 'flex', alignItems: 'center', justifyContent: 'end', position: 'relative'}}>
              {RIF(
                showAddTaskBackIntoAppTooltip,
                <div
                  css={{
                    width: '221px',
                    padding: '10px 8px',
                    textAlign: 'center',
                    backgroundColor: color.black,
                    color: color.white,
                    position: 'absolute',
                    top: '40px',
                    right: '0',
                    borderRadius: '5px',
                  }}>
                  Make this task active and editable again.
                </div>,
              )}
              <Button
                css={{width: '205px'}}
                btnColor='grey_20'
                textColor={color.grey_600}
                btnPadding='medium'
                type='button'
                onClick={() => {
                  if (taskInLiveEditing) unarchiveTask(taskInLiveEditing)
                }}
                onMouseEnter={() => setShowAddTaskBackIntoAppTooltip(true)}
                onMouseLeave={() => setShowAddTaskBackIntoAppTooltip(false)}>
                Unarchive This Task
              </Button>
            </div>,
          )}
        </div>

        {/* ------------------------- page container ------------------------- */}
        <div css={{display: 'flex'}}>
          {/* -------------- left section------------- */}
          <section
            css={{
              width: '26%',
              // height: '100%',
              height: '100vh',
              overflowY: 'scroll',
              padding: '40px 50px 0 80px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: 'column',
              borderRight: `1px solid ${color.grey_100}`,
              '::-webkit-scrollbar': {
                display: 'none',
              },
            }}>
            {/* left top */}
            <div>
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  paddingTop: '30px',
                  lineHeight: '1.857rem',
                }}>
                {RIF(taskScheduleSettingTypeToDisplayString(taskScheduleSettingType), (taskScheduleDisplayString) => (
                  <p
                    css={{
                      fontSize: fontSize.h6,
                      fontWeight: fontWeight.thick,
                      color: color.textIcon.link,
                      marginBottom: '20px',
                    }}>
                    {taskScheduleDisplayString}
                  </p>
                ))}
                <div
                  css={{
                    height: '26px',
                    width: '26px',
                    marginRight: pad.mediumSmall,
                    display: 'inline-block',
                    marginBottom: taskType === TaskType.StopwatchMovesenseStream ? '38px' : '8px',
                  }}>
                  {RIF(taskType === TaskType.Todo, <img src={TodoBlackIcon} width='26' />)}
                  {RIF(taskType === TaskType.Timer, <img src={TimerBlackIcon} width='26' />)}
                  {RIF(taskType === TaskType.Questionnaire, <img src={SurveyBlackIcon} width='26' />)}
                  {RIF(
                    taskType === TaskType.StopwatchMovesenseStream,
                    <img src={MovesenseImg} width='56' css={{transform: 'scale(1.2)'}} />,
                  )}
                  {RIF(taskType === TaskType.StopwatchGarminStream, <img src={DeviceIcon} width='26' />)}
                </div>
                <div
                  css={{
                    fontSize: '20px',
                    fontWeight: fontWeight.thick,
                  }}>
                  {isEditing?.id ? 'Edit' : 'Add'} {taskTypeTranslator[taskType]}
                </div>
              </div>

              {RIF(
                taskType === TaskType.Todo,
                <div
                  css={{
                    fontWeight: fontWeight.regular,
                    marginTop: '20px',
                  }}>
                  To-Do Tasks are actions for participants to complete once or repeatedly during the project.
                  <br />
                  <br />
                  There will be a notification when this task becomes available. If the task is nearing expiration and
                  still incomplete, it will also trigger reminders (at the 1 hour mark and the 15 minute mark).
                  <br />
                  <br />
                  <div>
                    <TaskExampleContainer>
                      {taskExampleContentBuilder(
                        'Schedule a daily morning intervention such as taking a certain medication.',
                        {
                          title: 'Task Title',
                          description: 'Morning Medication',
                        },
                        {
                          title: 'Description',
                          description: 'Complete this task after you take your medication.',
                        },
                        {
                          title: 'Schedule',
                          description: 'Daily starting at 6am and expires at 11am.',
                        },
                      )}
                    </TaskExampleContainer>
                  </div>
                </div>,
              )}

              {RIF(
                taskType === TaskType.Timer,
                <div
                  css={{
                    fontWeight: fontWeight.regular,
                    marginTop: '20px',
                  }}>
                  A Timer Task is a timed activity for participants to complete.
                  <br />
                  <br />
                  There will be a notification when this task becomes available. If the task is nearing expiration and
                  still incomplete, it will also trigger reminders (at the 1 hour mark and the 15 minute mark).
                  <br />
                  <br />
                  <div>
                    <TaskExampleContainer>
                      {taskExampleContentBuilder(
                        'Schedule a daily morning intervention such as an exercise routine but also timestamp and track the duration.',
                        {
                          title: 'Task Title',
                          description: 'Morning Medication',
                        },
                        {
                          title: 'Description',
                          description:
                            'Start this timer when you start your daily exercise. Stop and mark complete when the exercise finishes.',
                        },
                        {
                          title: 'Schedule',
                          description: 'Daily starting at 6am and expires at 11am.',
                        },
                      )}
                    </TaskExampleContainer>
                  </div>
                </div>,
              )}

              {RIF(
                taskType === TaskType.Questionnaire,
                <div
                  css={{
                    fontWeight: fontWeight.regular,
                    marginTop: '20px',
                  }}>
                  Use this to have participants complete a scheduled questionnaires.
                  <br />
                  <br />
                  There will be a notification when this task becomes available. If the task is nearing expiration and
                  still incomplete, it will also trigger reminders (at the 1 hour mark and the 15 minute mark).
                  <br />
                  <br />
                  <div>
                    <TaskExampleContainer index={1}>
                      {taskExampleContentBuilder(
                        'Track participants’ mental health throughout the day through an EMA scheduled questionnaire',
                        {
                          title: 'Task Title',
                          description: 'EMA Questionnaire',
                        },
                        {
                          title: 'Description',
                          description:
                            'Complete this questionnaire when you receive the notification. If you miss it because you are busy, it is okay but try your best. You only have 30 minutes to complete this.',
                        },
                        {
                          title: 'Schedule',
                          description:
                            'Daily; Semi-randomized between 9am-9pm; 2 hour intervals with 30m flexibility window; 30m expiration',
                        },
                      )}
                    </TaskExampleContainer>
                    <TaskExampleContainer index={2}>
                      {taskExampleContentBuilder(
                        'Understand and categorize participants through a baseline survey',
                        {
                          title: 'Task Title',
                          description: 'Onboarding Survey',
                        },
                        {
                          title: 'Description',
                          description: 'Complete this onboarding survey before the first week.',
                        },
                        {
                          title: 'Schedule',
                          description: 'Daily; Start Day 1 and expire on Day 7 at 23:59.',
                        },
                      )}
                    </TaskExampleContainer>
                  </div>
                </div>,
              )}

              {RIF(
                taskType === TaskType.StopwatchMovesenseStream,
                <div
                  css={{
                    fontWeight: fontWeight.regular,
                    marginTop: '20px',
                  }}>
                  This device is generally used for two purposes (which also can be combined). IMU movement sensor
                  and/or ECG heart monitor.
                  <br />
                  <br />
                  To use the <span css={{fontWeight: '700'}}>movement</span> function, first decide where you want to
                  track the movement. Then turn on the subsequent settings. Ex: Wear on wrist to track hand tremors.
                  <br />
                  <br />
                  To use the <span css={{fontWeight: '700'}}>heart rate</span> function (used for things like heart rate
                  variability) use it with the chest band accessory or chest patch. Make sure to turn on the ECG or RR
                  data type collection.
                  <br />
                  <br />
                  To use both functions combined, remember that you must wear the device either using the chest strap or
                  patch. Then, just remember to turn on the IMU data settings.
                  <br />
                  <br />
                  For more examples, visit our{' '}
                  <a
                    href='https://help.labfront.com/movesense-examples'
                    target='_blank'
                    css={{
                      fontWeight: '700',
                      color: color.primary,
                    }}>
                    Help Center
                  </a>
                </div>,
              )}

              {RIF(
                taskType === TaskType.StopwatchGarminStream,
                <div
                  css={{
                    fontWeight: fontWeight.regular,
                    marginTop: '20px',
                  }}>
                  This is a Device Streaming Task to capture accelerometer data from your Garmin device.
                  <br />
                  <br />
                  <p css={{fontWeight: '600'}}>Important Things to Consider:</p>
                  <br />
                  1. Due to the overwhelming amount of data captured by this sensor, participants will need to manually
                  start and stop the data collection.
                  <br />
                  <br />
                  2. Tasks are limited to 12 hours in duration.
                  <br />
                  <br />
                  3.Unlike continuous data collection, streaming data is NOT stored on the Garmin device. Therefore,
                  participants must have their Bluetooth turned on and keep their phone near their device to record
                  data.
                  <br />
                  <br />
                  For more information, please check out our{' '}
                  <a href='https://help.labfront.com/garmin-acc-streaming' css={{color: color.primary}}>
                    Help Center
                  </a>{' '}
                  article.
                </div>,
              )}
            </div>
          </section>

          {/* -------------- middle section------------- */}
          <section
            css={{
              width: '48%',
              // minHeight: '100%',
              height: '100vh',
              overflowY: 'scroll',
              background: color.background,
              padding: '80px 80px 30px 50px',
            }}>
            {/* Name */}
            <div>
              <LabelBox
                {...{
                  label: 'Name',
                  required: true,
                  leftCharacters: isNaN(50 - nameLength) ? 50 : 50 - nameLength,
                }}>
                <Input
                  {...register('name', {required: true})}
                  autoComplete='off'
                  value={isEditing?.[getTaskContentName(taskType)]?.name}
                  maxLength='50'
                  readOnly={!hasCreatePermission || disableLiveEdit}
                  hasError={formState.errors.name ? true : false}
                  placeholder={taskPlaceholder(taskType).taskNamePlaceholder}
                />
              </LabelBox>
            </div>

            {/* Description */}
            <div css={{marginTop: '40px'}}>
              <LabelBox
                {...{
                  label: 'Description',
                  required: true,
                  leftCharacters: isNaN(250 - descriptionLength) ? 250 : 250 - descriptionLength,
                }}>
                <Controller
                  control={control}
                  name='description'
                  defaultValue={isEditing?.[getTaskContentName(taskType)]?.description}
                  render={({field: {name, value, onChange}}) => (
                    <Textarea
                      {...{
                        name,
                        value: value ?? '',
                        onChange: onChange,
                      }}
                      placeholder={taskPlaceholder(taskType).taskDescriptionPlaceholder}
                      rows={6}
                      maxLength={250}
                      readOnly={!hasCreatePermission || disableLiveEdit}
                      hasError={formState.errors.description ? true : false}
                    />
                  )}
                />
              </LabelBox>
            </div>

            {/* Color */}
            <div css={{margin: '40px 0'}}>
              <LabelBox {...{label: 'Color', required: true}}>
                <ColorSelector
                  {...{
                    taskColor,
                    setTaskColor,
                  }}
                />
              </LabelBox>
            </div>

            {/* Questionnaire */}
            {RIF(
              taskType === TaskType.Questionnaire,
              <div css={{paddingTop: '40px', marginBottom: '40px', borderTop: `1px solid ${color.grey_100}`}}>
                <div css={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 8}}>
                  <img src={SurveyBlackIcon} width={20} height={20} />
                  <span>Questionnaire</span>
                  <RequiredTag />
                </div>
                <p css={{color: color.textIcon.secondary, marginBottom: 24}}>
                  Create a new questionnaire or select existing questionnaire from other projects.
                </p>
                {RIF(
                  !questionnaire,
                  <div
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}>
                    <Button btnPadding='mediumSmall' onClick={createQuestionnaireDraft}>
                      + Create New
                    </Button>
                    <ButtonReverse btnPadding='mediumSmall' onClick={fetchAllQuestionnaire}>
                      Duplicate Existing Questionnaire
                    </ButtonReverse>
                  </div>,
                )}
                {RIF(
                  questionnaire,
                  <div
                    onMouseEnter={() => {
                      if (!editQuestionnaireButtonEnabled) setRenderLiveEditQuestionnaireTooltip(true)
                    }}
                    onMouseLeave={() => {
                      if (!editQuestionnaireButtonEnabled) setRenderLiveEditQuestionnaireTooltip(false)
                    }}
                    css={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      height: '42px',
                      borderRadius: '5px',
                      backgroundColor: color.white,
                      padding: '12px 16px',
                      boxShadow: '0px 4px 12px rgba(213, 213, 213, 0.25)',
                    }}>
                    <button
                      disabled={!editQuestionnaireButtonEnabled}
                      onClick={editQuestionnaire}
                      type='button'
                      css={{
                        width: '80%',
                        height: '42px',
                        display: 'flex',
                        alignItems: 'center',
                        border: 'none',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                      }}>
                      <p css={{fontWeight: fontWeight.bold}}>
                        {questionnaire?.name ?? nameField ?? 'Untitled Questionnaire'}
                      </p>
                    </button>
                    {RIF(
                      editQuestionnaireButtonEnabled,
                      <div>
                        <button
                          onClick={deleteQuestionnaire}
                          type='button'
                          css={{
                            fontSize: fontSize.h7,
                            color: color.textIcon.secondary,
                            padding: '4px 8px',
                            borderRadius: 3,
                            border: 'none',
                            cursor: 'pointer',
                            backgroundColor: color.surface.white,
                            ':hover': {
                              backgroundColor: color.surface.hover.hoverWhiteBg,
                            },
                          }}>
                          Delete This Task
                        </button>
                      </div>,
                    )}
                    {RIF(
                      renderLiveEditQuestionnaireTooltip,
                      <div
                        css={{
                          position: 'absolute',
                          right: '-64px',
                          bottom: '40px',
                          width: '221px',
                          borderRadius: '5px',
                          padding: '10px 8px',
                          textAlign: 'center',
                          background: color.black,
                          color: color.white,
                        }}>
                        Questionnaires cannot be edited while a project is in progress. <br />
                        If you wish to change a questionnaire, create a new task with a new questionnaire instead, then
                        hide this questionnaire task.
                      </div>,
                    )}
                  </div>,
                )}
              </div>,
            )}

            {/* New Schedule */}
            {RIF(
              taskScheduleSettingType === TaskScheduleSettingType.Schedule,
              <TaskScheduleSection
                {...{
                  scheduleList: taskScheduleList,
                  taskName: nameField || isEditing?.[getTaskContentName(taskType)]?.name || 'Task',
                  onRequestScheduleCreate: handleTaskScheduleCreate,
                  onRequestScheduleUpdate: handleTaskScheduleUpdate,
                  onRequestScheduleDelete: handleTaskScheduleDelete,
                  onRequestScheduleReminderListUpdate: handleTaskScheduleReminderListUpdate,
                  isViewOnly: disableLiveEdit,
                  liveEditing: isEditingLiveProject,
                }}
              />,
            )}
            {/* Schedule */}
            {RIF(
              taskScheduleSettingType === TaskScheduleSettingType.Legacy,
              <AddTaskScheduleSection
                {...{
                  control,
                  register,
                  watch,
                  formState,
                  taskScheduleReadonly,
                  setTaskScheduleReadonly,
                  scheduleDescriptionLength,
                  setScheduleDescriptionLength,
                  isEditing,
                }}
              />,
            )}

            {/* movesense */}
            {RIF(
              taskType === TaskType.StopwatchMovesenseStream || taskType === TaskType.StopwatchMovesenseLogData,
              <AddTaskMovesenseSection
                {...{
                  movesenseStreamTaskCopy,
                  setMovesenseStreamTaskCopy,
                  movesenseReadOnly,
                  setDisplayChangePlanPage,
                }}
              />,
            )}

            {/* garmin stream */}
            {RIF(
              taskType === TaskType.StopwatchGarminStream,
              <AddTaskGarminStreamSection
                {...{
                  garminStreamTaskCopy,
                  setGarminStreamTaskCopy,
                  garminStreamReadOnly,
                  setDisplayChangePlanPage,
                }}
              />,
            )}

            {/* Reminder */}
            {RIF(
              taskScheduleSettingType === TaskScheduleSettingType.Legacy,
              <Controller
                control={control}
                name='reminderList'
                defaultValue={isEditing?.reminderList ?? []}
                render={({field: {value, onChange}}) => (
                  <AddTaskReminderSection
                    {...{
                      reminderList: value ?? [],
                      setReminderList: onChange,
                    }}
                  />
                )}
              />,
            )}
          </section>

          {/* -------------- right section------------- */}
          <section css={{width: '26%', background: color.grey_80}}>
            <AddTaskAppPreviewSection
              {...{
                taskPreviewContent: {
                  name: nameField,
                  description: descriptionField,
                  scheduleRepeat: scheduleRepeatField,
                  scheduleDescription: scheduleDescriptionField,
                },
                taskType,
                taskColor,
                reminderList: reminderListField ?? [],
                hideSchedulePreview: taskScheduleSettingType !== TaskScheduleSettingType.Legacy,
              }}
            />
          </section>
        </div>
      </form>
      {renderDuplicateExistingQuestionnairePopup && (
        <PopupDuplicateExistingQuestionnaire
          {...{
            setRenderDuplicateExistingQuestionnairePopup,
            questionnaireList,
            createQuestionnaireDraftFromOtherQuestionnaire,
          }}
        />
      )}
      {RIF(
        showPopupConfirmDeleteTask,
        <PopupConfirmDeleteTask
          {...{
            taskName: isEditing?.[getTaskContentName(taskType)]?.name ?? 'Task',
            closeAction: setShowPopupConfirmDeleteTask,
            deleteAction: deleteTask,
          }}
        />,
      )}
      {RIF(
        showPopupConfirmDiscardTask,
        <PopupConfirmDiscardTask
          {...{
            isLiveEditing: isEditingLiveProject,
            closeAction: setShowPopupConfirmDiscardTask,
            discardAction: closePopupAction,
          }}
        />,
      )}
      {RIF(
        showPopupConfirmShowTask,
        <PopupConfirmShowTask
          {...{
            closeAction: setShowPopupConfirmShowTask,
            showAction: handleShowHiddenTask,
          }}
        />,
      )}
      {RIF(
        renderQuestionnairePage,
        <QuestionnairePage
          {...{
            questionnaire,
            setQuestionnaire,
            viewOnly: false,
            isEditing,
            closeAction: () => setRenderQuestionnairePage(false),
            upsertDisplayStrategyList,
            setUpsertDisplayStrategyList,
          }}
        />,
      )}
    </>
  )
}
